import {
  createTranslator,
  DEFAULT_LOCALE,
  isReservedSlug,
  LOCALES,
  LOCALE_TAGS,
  type Locale,
  type TranslateFn,
  type TranslationKey,
} from '@nk/shared';
import { nonceAttribute } from '@/lib/route-data';

export interface PageMeta {
  title: string;
  description: string;
  /** Path without origin; the server turns it into an absolute canonical. */
  path: string;
  ogType: 'website' | 'article';
  noindex?: boolean;
  /** HTTP status the server should send. A 404 must render as a page *and* answer 404. */
  status: number;
  /** `no-store` for anything personalised or administrative — phases.md §1.11. */
  cacheControl: string;

  // --- Phase 15 (PRD §26) ----------------------------------------------------
  /** Absolute URL, already resolved by the server — the featured image or a default. */
  ogImage?: string | null;
  /** Overrides the canonical the path would imply (a district story, ADR-012). */
  canonical?: string | null;
  /** hreflang targets keyed by locale, when the counterpart is at a different slug. */
  alternates?: Record<string, string>;
  publishedTime?: string | null;
  modifiedTime?: string | null;
  section?: string | null;
  verification?: { google?: string | null; bing?: string | null };
  /** The publication's own robots default, when it is not `noindex`. */
  robots?: string | null;
  twitterSite?: string | null;
  /** Structured data, serialized into `application/ld+json` blocks. */
  jsonLd?: unknown[];
}

const SITE_NAME: Record<Locale, string> = {
  as: 'NK নিউজ টাইম',
  en: 'NK News Time',
};

/** The publication's name in the reader's language — every title ends with it. */
export function siteName(locale: Locale): string {
  return SITE_NAME[locale];
}

/**
 * The tagline the identity carries — "অসমৰ কথা / A STRONGER ASSAM" — which is what the
 * logo lockup shows. Title case in English rather than the logo's capitals: this string
 * ends up in a `<title>`, and an all-caps title reads as shouting in a search result.
 */
const TAGLINE: Record<Locale, string> = {
  as: 'অসমৰ কথা',
  en: 'A Stronger Assam',
};

const HOME_DESCRIPTION: Record<Locale, string> = {
  as: 'অসম, ভাৰত আৰু বিশ্বৰ বিশ্বাসযোগ্য আৰু নিৰপেক্ষ বাতৰিৰ আপোনাৰ ভৰসাযোগ্য উৎস।',
  en: 'Your trusted source for reliable and unbiased news from Assam, India, and around the world.',
};

const PUBLIC_CACHE = 'public, s-maxage=60, stale-while-revalidate=300';
const ARTICLE_CACHE = 'public, s-maxage=300, stale-while-revalidate=300';

/**
 * The static routes, and the dictionary key each takes its title from. This mirrors the
 * route table in `App.tsx`, and `meta.test.ts` asserts the two agree: a page that renders
 * with the wrong `<title>`, or answers 404 while looking fine in a browser, is invisible
 * to exactly the crawlers §1.10 exists to satisfy.
 */
const STATIC_ROUTES: Record<string, TranslationKey> = {
  search: 'search.title',
  trending: 'section.trending',
  newsletter: 'newsletter.title',
  videos: 'section.videos',
  live: 'section.liveUpdates',
  local: 'footer.localNews',
  about: 'footer.about',
  contact: 'footer.contact',
  privacy: 'footer.privacy',
  terms: 'footer.terms',
  advertise: 'footer.advertise',
};

/**
 * The fallback description for a `/tag/x`, `/author/x`, `/local/x` or `/videos/x` page.
 *
 * A tag archive and an author archive are the two that most need it: both are lists of
 * headlines with no prose of their own, so without this they share the publication's
 * default description with every other archive on the site.
 */
function descriptionForSlugPage(parent: string, name: string, t: TranslateFn): string {
  if (parent === 'tag') return t('meta.tagDescription', { name });
  if (parent === 'author') return t('meta.authorDescription', { name });
  if (parent === 'local') return t('meta.categoryDescription', { name });
  if (parent === 'videos') return t('meta.videosDescription');
  if (parent === 'live') return t('meta.liveDescription');
  return '';
}

/**
 * A description per static route — Phase 17.
 *
 * Every one of these pages shipped without a `meta description` until Lighthouse was
 * run on something other than the homepage and an article: the `page()` helper below
 * defaulted to an empty string, so the video grid, the live index, search, trending and
 * the newsletter all scored 92 or worse on SEO for the same missing tag.
 *
 * These are *fallbacks*. A page whose data carries a real description — a category with
 * a standfirst, an article, a live event — overrides them in `refineMeta`.
 */
const STATIC_DESCRIPTIONS: Record<string, TranslationKey> = {
  search: 'meta.searchDescription',
  trending: 'meta.trendingDescription',
  newsletter: 'meta.newsletterDescription',
  videos: 'meta.videosDescription',
  live: 'meta.liveDescription',
  local: 'meta.localDescription',
};

/** Prefixes whose second segment is a slug rather than another page. */
const SLUG_PARENTS = new Set(['videos', 'live', 'local', 'tag', 'author']);

/**
 * Resolves metadata for a route — phases.md §1.10.
 *
 * This runs on the server *before* rendering, never in an effect. That ordering is the
 * whole reason the SEO problem is solved by the architecture instead of worked around:
 * by the time the first byte leaves, the correct title and OG tags are already in the head.
 *
 * Phase 7 makes the resolution **shape-based**: it knows which URL shapes are real routes
 * and answers 200 for those, 404 for everything else. It cannot yet know whether
 * `/as/sports` names a category that *exists*, because that is data — Phase 10 resolves
 * the slug against the API and turns an unknown one into a real 404, and Phase 15
 * replaces these titles with per-article metadata. The contract this function honours
 * must not change.
 */
export function resolveMeta(pathname: string, locale: Locale): PageMeta {
  const t = createTranslator(locale);
  const rest = stripLocale(pathname);
  const segments = rest.split('/').filter(Boolean);
  const [first, second, third] = segments;

  const page = (
    title: string,
    options: Partial<Omit<PageMeta, 'title' | 'path'>> = {},
  ): PageMeta => ({
    title: `${title} — ${SITE_NAME[locale]}`,
    description: '',
    path: pathname,
    ogType: 'website',
    status: 200,
    cacheControl: PUBLIC_CACHE,
    ...options,
  });

  if (first === 'admin') {
    return page(t('nav.menu'), { noindex: true, cacheControl: 'no-store' });
  }

  if (segments.length === 0) {
    return {
      title: `${SITE_NAME[locale]} — ${TAGLINE[locale]}`,
      description: HOME_DESCRIPTION[locale],
      path: pathname,
      ogType: 'website',
      status: 200,
      cacheControl: PUBLIC_CACHE,
    };
  }

  // The kitchen sink is a development surface: reachable, never indexed, never cached.
  if (first === 'dev') {
    return second === 'kitchen-sink'
      ? page('Kitchen sink', { noindex: true, cacheControl: 'no-store' })
      : notFound(pathname, locale, t);
  }

  if (segments.length === 1 && first) {
    const staticKey = STATIC_ROUTES[first];
    if (staticKey) {
      const descriptionKey = STATIC_DESCRIPTIONS[first];
      const described = descriptionKey ? { description: t(descriptionKey) } : {};
      // A results page is infinite, thin and duplicates the listings it draws from.
      // Phase 15 owns the rest of the robots story; this one is not worth waiting for.
      return first === 'search'
        ? page(t(staticKey), { ...described, noindex: true })
        : page(t(staticKey), described);
    }
    // A reserved word with no route behind it is a 404, not a category.
    if (isReservedSlug(first)) return notFound(pathname, locale, t);
    const name = humanize(first);
    return page(name, { description: t('meta.categoryDescription', { name }) });
  }

  if (segments.length === 2 && first && SLUG_PARENTS.has(first)) {
    const parentKey = STATIC_ROUTES[first];
    return page(
      first === 'tag' || first === 'author'
        ? humanize(second ?? '')
        : parentKey
          ? t(parentKey)
          : humanize(second ?? ''),
      {
        ogType: first === 'videos' ? 'article' : 'website',
        description: descriptionForSlugPage(first, humanize(second ?? ''), t),
        // A live timeline changes by the minute; §1.11 keeps it out of shared caches.
        cacheControl: first === 'live' ? 'no-store' : PUBLIC_CACHE,
      },
    );
  }

  if (first && isReservedSlug(first)) return notFound(pathname, locale, t);

  // §1.6 content shapes: /:category/:slug and /:category/:district/:slug.
  if (segments.length === 2 || segments.length === 3) {
    return page(humanize(third ?? second ?? ''), {
      ogType: 'article',
      cacheControl: ARTICLE_CACHE,
    });
  }

  return notFound(pathname, locale, t);
}

function notFound(pathname: string, locale: Locale, t: (key: TranslationKey) => string): PageMeta {
  return {
    title: `${t('error.notFoundTitle')} — ${SITE_NAME[locale]}`,
    description: '',
    path: pathname,
    ogType: 'website',
    noindex: true,
    status: 404,
    cacheControl: 'public, s-maxage=60',
  };
}

/** `north-assam-railway` → `North assam railway`, a stand-in until Phase 9 has real titles. */
function humanize(slug: string): string {
  const spaced = slug.replace(/-/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** `/as/sports/foo` → `/sports/foo` */
export function stripLocale(pathname: string): string {
  for (const locale of LOCALES) {
    if (pathname === `/${locale}`) return '';
    if (pathname.startsWith(`/${locale}/`)) return pathname.slice(locale.length + 1);
  }
  return pathname;
}

/** Locale-swapped variants of a path, for `hreflang` and the language switcher. */
export function alternatePaths(pathname: string): Record<Locale, string> {
  const rest = stripLocale(pathname) || '';
  return LOCALES.reduce(
    (acc, locale) => ({ ...acc, [locale]: `/${locale}${rest}` }),
    {} as Record<Locale, string>,
  );
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Only the active locale's fonts are preloaded (§1.15). An Assamese reader must not
 * download Playfair Display to render a page with no Latin headline in it, and an English
 * reader must not download 292 KB of Bengali to render none.
 *
 * `inter-latin` is preloaded in both: the Assamese stack puts Inter first, so digits,
 * datelines and names like ISRO render in it on an Assamese page too. Noto Serif Bengali
 * is deliberately *not* preloaded — it is the largest file of the set and only headlines
 * use it, so it loads with `font-display: swap` rather than delaying first paint.
 */
const PRELOAD_FONTS: Record<Locale, string[]> = {
  // An Assamese page preloads one font, not two. Inter is first in its stack and renders
  // the Latin runs, but preloading it costs 49 KB of high-priority bandwidth on a
  // connection already fetching 108 KB of Noto Sans and the hero image. It arrives with
  // `swap` instead, which for a few Latin words is the right trade.
  as: ['/fonts/noto-sans-bengali-bengali.woff2'],
  en: ['/fonts/inter-latin.woff2', '/fonts/playfair-display-latin.woff2'],
};

/**
 * Serializes metadata into head HTML. Kept as a string builder rather than a React
 * component because the head must be flushed before the body stream opens.
 */
/** The route's LCP image, preloaded ahead of the body stream (Phase 8). */
export interface HeroPreload {
  href: string;
  srcset?: string;
  sizes?: string;
  type?: string;
}

export interface HeadOptions {
  preloadImage?: HeroPreload | undefined;
  /** The request's CSP nonce (Phase 17). Empty in development, where CSP is off. */
  nonce?: string | undefined;
}

/** `{ as: '/as/x' }` → `{ as: 'https://…/as/x' }`, so both shapes reach the same code. */
function absolutePaths(paths: Record<string, string>, origin: string): Record<string, string> {
  return Object.fromEntries(Object.entries(paths).map(([key, path]) => [key, origin + path]));
}

export function renderHead(
  meta: PageMeta,
  locale: Locale,
  origin: string,
  options: HeadOptions = {},
): string {
  // A page may know better than its path does: a district story is canonical at the
  // district-scoped address the API returns (ADR-012), and an article's translation
  // lives at its own slug rather than the same path in the other locale.
  const alternates = meta.alternates ?? absolutePaths(alternatePaths(meta.path), origin);
  const canonical = meta.canonical ?? `${origin}${meta.path}`;
  const tags: string[] = [
    `<title>${escapeHtml(meta.title)}</title>`,
    `<link rel="canonical" href="${escapeHtml(canonical)}" />`,
    ...PRELOAD_FONTS[locale].map(
      (href) =>
        `<link rel="preload" href="${href}" as="font" type="font/woff2" crossorigin="anonymous" />`,
    ),
  ];

  const image = options.preloadImage;
  if (image) {
    const attrs = [
      'rel="preload"',
      'as="image"',
      `href="${escapeHtml(image.href)}"`,
      image.srcset ? `imagesrcset="${escapeHtml(image.srcset)}"` : '',
      image.sizes ? `imagesizes="${escapeHtml(image.sizes)}"` : '',
      image.type ? `type="${escapeHtml(image.type)}"` : '',
      'fetchpriority="high"',
    ].filter(Boolean);
    tags.push(`<link ${attrs.join(' ')} />`);
  }
  if (meta.description) {
    tags.push(`<meta name="description" content="${escapeHtml(meta.description)}" />`);
  }

  // Feed discovery: the link every reader app looks for (PRD §26).
  tags.push(
    `<link rel="alternate" type="application/rss+xml" title="${escapeHtml(siteName(locale))}" href="${origin}/rss.xml?locale=${locale}" />`,
  );
  if (meta.noindex) {
    tags.push('<meta name="robots" content="noindex, nofollow" />');
  } else if (meta.robots) {
    // The publication's own directive from the SEO settings (Stage 14d), for the pages
    // that are indexable. `noindex` above always wins — it is a decision about *this*
    // page rather than a default.
    tags.push(`<meta name="robots" content="${escapeHtml(meta.robots)}" />`);
  }

  // hreflang reciprocates across locales; x-default points at Assamese (phases.md §1.15).
  for (const [alt, href] of Object.entries(alternates)) {
    tags.push(`<link rel="alternate" hreflang="${alt}" href="${escapeHtml(href)}" />`);
  }
  const defaultHref = alternates[DEFAULT_LOCALE];
  if (defaultHref) {
    tags.push(`<link rel="alternate" hreflang="x-default" href="${escapeHtml(defaultHref)}" />`);
  }

  tags.push(
    `<meta property="og:type" content="${meta.ogType}" />`,
    `<meta property="og:title" content="${escapeHtml(meta.title)}" />`,
    `<meta property="og:url" content="${escapeHtml(canonical)}" />`,
    `<meta property="og:site_name" content="NK News Time" />`,
    `<meta property="og:locale" content="${LOCALE_TAGS[locale].replace('-', '_')}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${escapeHtml(meta.title)}" />`,
  );

  // The other locale, when this page actually has one — an `og:locale:alternate` for a
  // translation that does not exist is a claim a crawler will check.
  for (const alt of LOCALES) {
    if (alt === locale || !alternates[alt]) continue;
    tags.push(
      `<meta property="og:locale:alternate" content="${LOCALE_TAGS[alt].replace('-', '_')}" />`,
    );
  }

  if (meta.ogImage) {
    tags.push(
      `<meta property="og:image" content="${escapeHtml(meta.ogImage)}" />`,
      `<meta name="twitter:image" content="${escapeHtml(meta.ogImage)}" />`,
    );
  }
  if (meta.twitterSite) {
    tags.push(`<meta name="twitter:site" content="${escapeHtml(meta.twitterSite)}" />`);
  }

  if (meta.ogType === 'article') {
    if (meta.publishedTime) {
      tags.push(
        `<meta property="article:published_time" content="${escapeHtml(meta.publishedTime)}" />`,
      );
    }
    if (meta.modifiedTime) {
      tags.push(
        `<meta property="article:modified_time" content="${escapeHtml(meta.modifiedTime)}" />`,
      );
    }
    if (meta.section) {
      tags.push(`<meta property="article:section" content="${escapeHtml(meta.section)}" />`);
    }
  }

  if (meta.verification?.google) {
    tags.push(
      `<meta name="google-site-verification" content="${escapeHtml(meta.verification.google)}" />`,
    );
  }
  if (meta.verification?.bing) {
    tags.push(`<meta name="msvalidate.01" content="${escapeHtml(meta.verification.bing)}" />`);
  }
  if (meta.description) {
    tags.push(
      `<meta property="og:description" content="${escapeHtml(meta.description)}" />`,
      `<meta name="twitter:description" content="${escapeHtml(meta.description)}" />`,
    );
  }

  /**
   * Structured data last, and one block per object rather than one `@graph`: a malformed
   * entry then costs Google that entry instead of every entry on the page.
   *
   * `<` is escaped as `\u003c` inside the JSON — a headline containing `</script>` would
   * otherwise close the block, which is the same injection `serializeRouteData` guards.
   */
  for (const entry of meta.jsonLd ?? []) {
    const json = JSON.stringify(entry).replace(/</g, '\\u003c');
    // Nonced like any other inline script: `application/ld+json` is not executed, but
    // `script-src` governs the element rather than what it contains, and a blocked
    // block is invisible structured data rather than a visible error.
    tags.push(
      `<script type="application/ld+json"${nonceAttribute(options.nonce)}>${json}</script>`,
    );
  }

  return tags.join('\n    ');
}
